// GlimtEditor — fullskjerm preview av tatt bilde/video med:
// - Draggable tekst-overlay (én font, 8 farger)
// - Segment-velger
// - Publiser-knapp
// Bilde brennes inn via canvas før upload. Video sendes med overlay-data (server-side ffmpeg burn-in kan legges til senere; foreløpig lagres overlay-data i doc).
import React, { useEffect, useRef, useState } from "react";
import { X, Type, Send, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const TEXT_COLORS = ["#FFFFFF", "#000000", "#E91E63", "#FF69B4", "#FFEB3B", "#00E5FF", "#76FF03", "#FF9100"];

export default function GlimtEditor({ capture, onClose, onPublished }) {
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [text, setText] = useState("");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [textPos, setTextPos] = useState({ x: 0.5, y: 0.5 }); // 0..1 relative
  const [fontScale, setFontScale] = useState(1);
  const [editingText, setEditingText] = useState(false);
  const [visibility, setVisibility] = useState("followers");
  const [segments, setSegments] = useState([]);
  const [selectedSegs, setSelectedSegs] = useState([]);
  const [replayMode, setReplayMode] = useState("once");
  const [publishing, setPublishing] = useState(false);
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!capture?.blob) return;
    const url = URL.createObjectURL(capture.blob);
    setMediaUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [capture]);

  useEffect(() => {
    api.get("/me/glimt-segments").then((r) => setSegments(r.data.items || [])).catch(() => {});
  }, []);

  // Drag håndtering for tekst
  const onTextPointerDown = (e) => {
    if (editingText) return;
    e.preventDefault();
    const c = containerRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    dragRef.current = { rect, startX: e.clientX, startY: e.clientY, startPos: textPos };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const { rect, startX, startY, startPos } = dragRef.current;
    const dx = (e.clientX - startX) / rect.width;
    const dy = (e.clientY - startY) / rect.height;
    setTextPos({
      x: Math.max(0.05, Math.min(0.95, startPos.x + dx)),
      y: Math.max(0.05, Math.min(0.95, startPos.y + dy)),
    });
  };
  const onPointerUp = () => { dragRef.current = null; };

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  const publish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      let uploadBlob = capture.blob;
      let uploadFilename = "glimt.jpg";
      let uploadType = "image/jpeg";

      if (capture.type === "image" && text.trim()) {
        // Brenn inn tekst i canvas for bilder
        const img = new Image();
        img.src = mediaUrl;
        await new Promise((res) => { img.onload = res; });
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const fontSize = Math.round(w * 0.06 * fontScale);
        ctx.font = `700 ${fontSize}px system-ui, -apple-system, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = textColor;
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.lineWidth = Math.max(2, fontSize / 12);
        const cx = textPos.x * w;
        const cy = textPos.y * h;
        // Wrap linjer
        const lines = text.split("\n");
        const lh = fontSize * 1.15;
        lines.forEach((ln, i) => {
          const y = cy + (i - (lines.length - 1) / 2) * lh;
          ctx.strokeText(ln, cx, y);
          ctx.fillText(ln, cx, y);
        });
        uploadBlob = await new Promise((r) => canvas.toBlob((b) => r(b), "image/jpeg", 0.92));
      } else if (capture.type === "video") {
        uploadFilename = "glimt.mp4";
        uploadType = capture.mimeType || "video/mp4";
        // Video: tekst-overlay lagres som doc-data (server-side burn-in kan legges til)
      }

      const fd = new FormData();
      fd.append("file", uploadBlob, uploadFilename);
      fd.append("caption", capture.type === "video" ? text.slice(0, 280) : "");
      fd.append("duration_hours", "24");
      fd.append("visibility", visibility);
      fd.append("replay_mode", replayMode);
      if (visibility === "segments" && selectedSegs.length) {
        fd.append("segment_ids", selectedSegs.join(","));
      }
      // Direct-to-R2 for video >20 MB → omgår Cloudflare/ingress 413
      const isBigVideo = capture.type === "video" && uploadBlob && uploadBlob.size > 20 * 1024 * 1024;
      if (isBigVideo) {
        const init = await api.post("/me/glimt/init-upload", {
          content_type: uploadType,
          size: uploadBlob.size,
          filename: uploadFilename,
          caption: text.slice(0, 280),
          duration_hours: 24,
          visibility,
          segment_ids: visibility === "segments" ? selectedSegs : [],
          replay_mode: replayMode,
        });
        const { video_id, upload_url } = init.data || {};
        if (!video_id || !upload_url) throw new Error("Init-upload feilet");
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", upload_url);
          xhr.setRequestHeader("Content-Type", uploadType);
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
            ? resolve()
            : reject(new Error(`R2 PUT feilet: ${xhr.status}`));
          xhr.onerror = () => reject(new Error("R2 PUT nettverksfeil (sjekk R2 CORS)"));
          xhr.send(uploadBlob);
        });
        const fin = await api.post("/me/glimt/finalize", { video_id });
        if (fin.data?.processing) {
          toast.success("Sendt inn. Prosesseres i bakgrunn — dukker opp om et par minutter.");
          onPublished?.();
          return;
        }
      } else {
        await api.post("/me/glimt/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Glimt publisert");
      onPublished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke publisere");
    } finally {
      setPublishing(false);
    }
  };

  const isVideo = capture?.type === "video";

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="glimt-editor">
      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        {isVideo ? (
          <video
            ref={previewRef}
            src={mediaUrl}
            playsInline
            autoPlay
            muted
            loop
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img src={mediaUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}

        {/* Tekst-overlay (drag) */}
        {text && !editingText && (
          <div
            onPointerDown={onTextPointerDown}
            className="absolute select-none touch-none px-3 py-1 font-bold text-center pointer-events-auto"
            style={{
              left: `${textPos.x * 100}%`, top: `${textPos.y * 100}%`,
              transform: "translate(-50%, -50%)",
              color: textColor,
              fontSize: `${28 * fontScale}px`,
              lineHeight: 1.15,
              textShadow: "0 2px 8px rgba(0,0,0,0.7)",
              whiteSpace: "pre-wrap",
              maxWidth: "80%",
            }}
            data-testid="glimt-text-overlay"
          >
            {text}
          </div>
        )}

        {/* Tekst-editor overlay */}
        {editingText && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-6" onClick={() => setEditingText(false)}>
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Skriv tekst…"
              className="w-full max-w-md bg-transparent border-0 outline-none text-center font-bold resize-none"
              style={{ color: textColor, fontSize: "28px", textShadow: "0 2px 8px rgba(0,0,0,0.7)" }}
              rows={3}
              data-testid="glimt-text-input"
            />
          </div>
        )}

        {/* Topp-rad */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white"
            data-testid="glimt-editor-close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={() => setEditingText((v) => !v)}
            className="px-3 h-10 rounded-full bg-black/40 backdrop-blur flex items-center gap-2 text-white text-sm"
            data-testid="glimt-add-text"
          >
            <Type className="w-4 h-4" /> Tekst
          </button>
        </div>

        {/* Farge-palett (kun når tekst finnes eller redigeres) */}
        {(text || editingText) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10">
            {TEXT_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setTextColor(c)}
                className={`w-7 h-7 rounded-full border-2 ${textColor === c ? "border-white" : "border-white/30"}`}
                style={{ backgroundColor: c }}
                data-testid={`glimt-color-${c.replace("#", "")}`}
                aria-label={`Farge ${c}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Nedre bar: synlighet + visning + publiser */}
      <div className="bg-black/90 border-t border-white/10 p-4 pb-6 space-y-3">
        <div className="flex items-center gap-2 overflow-x-auto">
          <VisChip
            active={visibility === "followers"}
            onClick={() => { setVisibility("followers"); setSelectedSegs([]); }}
            label="Alle følgere"
            testId="glimt-vis-followers"
          />
          {segments.map((s) => (
            <VisChip
              key={s.id}
              active={visibility === "segments" && selectedSegs.includes(s.id)}
              onClick={() => {
                setVisibility("segments");
                setSelectedSegs((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id]);
              }}
              label={s.name}
              testId={`glimt-vis-seg-${s.id}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          <VisChip
            active={replayMode === "once"}
            onClick={() => setReplayMode("once")}
            label="Se én gang"
            testId="glimt-replay-once"
          />
          <VisChip
            active={replayMode === "until_expires"}
            onClick={() => setReplayMode("until_expires")}
            label="Vises i 24 t"
            testId="glimt-replay-until-expires"
          />
        </div>
        <button
          onClick={publish}
          disabled={publishing || (visibility === "segments" && !selectedSegs.length)}
          className="w-full h-12 rounded-full bg-[var(--magenta)] text-black font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="glimt-publish"
        >
          {publishing ? "Publiserer…" : <><Send className="w-4 h-4" /> Publiser Glimt</>}
        </button>
      </div>
    </div>
  );
}

function VisChip({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 h-9 px-3 rounded-full text-xs font-meta tracking-wider whitespace-nowrap border ${
        active ? "bg-[var(--magenta)] text-black border-[var(--magenta)]" : "bg-black/40 text-[var(--cream)] border-white/20"
      }`}
      data-testid={testId}
    >
      {active && <Check className="inline w-3 h-3 mr-1" />}
      {label}
    </button>
  );
}
